"use client";

import React, { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: "What makes Smile4Me the best dentist in Astoria, Queens?",
    answer:
      "Smile4Me combines experienced care, advanced technology, and patient-centered service to deliver high-quality dental care.",
  },
  {
    question: "Do you offer emergency dentistry in Astoria, Queens?",
    answer:
      "Yes, we provide prompt emergency services for patients experiencing pain or urgent dental needs.",
  },
  {
    question: "How much do dental implants cost at Smile4Me?",
    answer:
      "The cost varies based on individual needs. We offer consultations to discuss pricing and payment options.",
  },
  {
    question: "What cosmetic dentistry services do you offer in Astoria?",
    answer:
      "Our cosmetic services include teeth whitening, veneers, and more to enhance your smile's appearance.",
  },
  {
    question: "Is Smile4Me conveniently located for Astoria residents?",
    answer:
      "Yes, we're centrally located in Astoria, Queens, making it easy for local residents to reach us.",
  },
  {
    question: "Do you offer payment plans for dental treatments?",
    answer:
      "Yes, we offer flexible payment plans to make dental care affordable.",
  },
  {
    question: "How often should I visit Smile4Me for cleanings?",
    answer:
      "We recommend scheduling cleanings every six months for optimal oral health.",
  },
  {
    question: "What are the benefits of Invisalign?",
    answer:
      "Invisalign provides a nearly invisible, comfortable way to straighten teeth without braces.",
  },
  {
    question: "Are there any dental services specific to adults?",
    answer:
      "Yes, we offer adult-focused treatments such as Invisalign and cosmetic options.",
  },
  {
    question: "Can you help with tooth pain?",
    answer:
      "Yes, we address tooth pain and provide treatments to alleviate discomfort.",
  },
  {
    question: "How long do dental implants last?",
    answer: "With proper care, dental implants can last a lifetime.",
  },
  {
    question: "What should I do in a dental emergency?",
    answer:
      "Call us immediately. Our emergency dentist will provide prompt care.",
  },
  {
    question: "Does Smile4Me accept new patients?",
    answer: "Yes, we welcome new patients from Astoria and surrounding areas.",
  },
  {
    question: "What is the difference between braces and Invisalign?",
    answer:
      "Invisalign is nearly invisible and removable, while traditional braces are fixed metal brackets.",
  },
  {
    question: "Do you offer teeth whitening?",
    answer:
      "Yes, we provide professional teeth whitening to brighten your smile.",
  },
  {
    question: "How do I know if I need a dental implant?",
    answer:
      "If you're missing a tooth or have a damaged tooth, a dental implant may be ideal.",
  },
  {
    question: "Can Smile4Me help with a chipped tooth?",
    answer:
      "Yes, we offer treatments for chipped or broken teeth to restore your smile.",
  },
  {
    question: "What should I expect during my first visit?",
    answer:
      "Your first visit includes a comprehensive exam, cleaning, and consultation.",
  },
  {
    question: "Is cosmetic dentistry painful?",
    answer:
      "Most cosmetic procedures are minimally invasive and cause little to no discomfort.",
  },
  {
    question: "How can I schedule an appointment?",
    answer:
      "Call us or book online. We're here to serve the Astoria community with quality dental care.",
  },
];

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <section
        className="relative w-full bg-gradient-to-br from-gray-50 to-white pt-32 pb-12 sm:pt-36 sm:pb-16 overflow-hidden"
        role="region"
        aria-labelledby="faq-heading"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        {/* Decorative background elements - matching other components */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#ECD05B]/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#AE7D2F]/10 to-transparent rounded-full blur-3xl" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8 lg:px-12">
          <h1
            id="faq-heading"
            className="
            text-3xl sm:text-4xl lg:text-5xl font-semibold text-gray-900 text-center
            after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:my-5
            after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]
            mb-12
          "
          >
            Frequently Asked Questions
          </h1>

          {/* FAQ Items */}
          <div className="space-y-2">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className="border-b border-dotted border-gray-300 last:border-b-0"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full py-4 flex items-center justify-between text-left group hover:cursor-pointer"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3
                    className="text-lg sm:text-xl font-normal text-gray-900 pr-4 group-hover:text-[#d5ac26] transition-colors duration-300"
                    itemProp="name"
                  >
                    {faq.question}
                  </h3>
                  <span
                    className={`
                    flex-shrink-0 text-2xl text-gray-900 group-hover:text-[#d5ac26] 
                    transition-all duration-300 font-light
                    ${openIndex === index ? "rotate-45" : ""}
                  `}
                  >
                    +
                  </span>
                </button>

                <div
                  id={`faq-answer-${index}`}
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? "max-h-96 pb-4" : "max-h-0"
                  }`}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                  hidden={openIndex !== index}
                >
                  <div
                    className="text-gray-700 leading-relaxed text-base"
                    itemProp="text"
                  >
                    <p>{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA Section */}
          <div className="mt-16 text-center">
            <p className="text-lg text-gray-700 mb-8">
              Still have questions? We&#39;re here to help!
            </p>
            <a
              href="/contact"
              className="
              uppercase font-bold text-lg tracking-wide text-white
              px-6 py-3
              bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F]
              hover:from-[#ECD05B] hover:via-[#AE7D2F] hover:to-[#ECD05B]
              rounded-lg
              shadow-lg hover:shadow-inner
              [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)]
              hover:scale-105
              inline-block
              transition-all duration-300 ease-in-out
            "
              onClick={() => console.log("Contact Us button clicked")}
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQPage;